import React from 'react'
import { GUIDE_START_DATE } from '../constants'
import { useTranslation } from 'react-i18next'
import { Typography, Divider } from '@mui/material'
import { selectNetworks } from '../selectors/networks'
import { selectPermissions } from '../selectors/organizations'
import { State, Dispatch } from '../store'
import { useSelector, useDispatch } from 'react-redux'
import { selectDeviceModelAttributes } from '../selectors/devices'
import { LinearProgress } from '../components/LinearProgress'
import { LoadingMessage } from '../components/LoadingMessage'
import { GuideBubble } from '../components/GuideBubble'
import { IconButton } from '../buttons/IconButton'
import { Container } from '../components/Container'
import { Network } from '../components/Network'
import { Gutters } from '../components/Gutters'
import { byName } from '../helpers/utilHelper'
import { Title } from '../components/Title'
import { Icon } from '../components/Icon'

export const NetworksPage: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch<Dispatch>()
  const all = [...useSelector(selectNetworks)].sort(byName)
  const initialized = useSelector((state: State) => state.networks.initialized)
  const permissions = useSelector(selectPermissions)
  const loading = useSelector(selectDeviceModelAttributes).fetching
  const networks = all.filter(n => !n.shared)
  const shared = all.filter(n => n.shared)
  const empty = !all?.length

  return (
    <Container
      bodyProps={{ verticalOverflow: true, gutterTop: true }}
      gutterBottom
      header={
        <>
          <Typography variant="subtitle1">
            <Title>{t('networksPage.title', 'Networks')}</Title>
            {permissions.includes('MANAGE') && (
              <GuideBubble
                guide="addNetwork"
                enterDelay={400}
                placement="bottom"
                startDate={GUIDE_START_DATE}
                added={GUIDE_START_DATE}
                instructions={
                  <>
                    <Typography variant="h3" gutterBottom>
                      <b>{t('networksPage.guideTitle', 'Welcome to networks')}</b>
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {t(
                        'networksPage.guideBody',
                        'A network is a custom group of services organized for ease of access and sharing.'
                      )}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {t('networksPage.guideCtaBefore', 'Select the')} <cite>+</cite>{' '}
                      {t('networksPage.guideCtaAfter', 'icon to get started.')}
                    </Typography>
                  </>
                }
              >
                <IconButton
                  icon="plus"
                  title={t('networksPage.newNetwork', 'New Network')}
                  to="/networks/add"
                  type="solid"
                  size="md"
                  shiftDown
                />
              </GuideBubble>
            )}
          </Typography>
          <LinearProgress loading={loading} />
        </>
      }
    >
      {initialized ? (
        <>
          {empty && (
            <Gutters top="xxl" bottom="xxl" center>
              <Typography variant="h1" gutterBottom>
                <Icon name="chart-network" fontSize={50} type="light" color="grayLight" />
              </Typography>
              <Typography variant="h3">{t('networksPage.empty', 'You have no networks')}</Typography>
              <Typography variant="caption">
                {t('networksPage.emptyHint', "Add a network with the '+' button above.")}
              </Typography>
            </Gutters>
          )}
          {networks.map(n => (
            <Network
              key={n.id}
              network={n}
              onClear={
                n.permissions.includes('MANAGE')
                  ? id => dispatch.networks.remove({ serviceId: id, networkId: n.id })
                  : undefined
              }
            />
          ))}
          {!!shared.length && (
            <>
              {!!networks.length && (
                <>
                  <Typography variant="subtitle1">{t('networksPage.sharedWithYou', 'Shared with you')}</Typography>
                  <Divider variant="inset" />
                </>
              )}
              {shared.map(n => (
                <Network key={n.id} network={n} />
              ))}
            </>
          )}
        </>
      ) : (
        <LoadingMessage message={t('common.loading', 'Loading...')} spinner={false} />
      )}
    </Container>
  )
}
